/**
 * The header's search field, and the suggestions under it.
 *
 * The field is a real <form> pointing at /search, so it works with this script
 * absent — typing and pressing Enter lands on the results page. What is added
 * is the panel: Shopify renders sections/predictive-search.liquid for the
 * current query and this drops the markup in.
 *
 * Rendering is deliberately left in Liquid. A suggestion is a card like any
 * other in the theme, and building one in JavaScript would mean two places to
 * change when it needs a price or a thumbnail.
 */
const MIN_QUERY = 2;
const DEBOUNCE_MS = 250;

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.panel = this.querySelector('[data-search-panel]');
    if (!this.input || !this.panel) return;

    this.requestId = 0;

    this.onInput = this.onInput.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onDocClick = this.onDocClick.bind(this);

    this.input.addEventListener('input', this.onInput);
    this.addEventListener('keydown', this.onKeydown);
    document.addEventListener('click', this.onDocClick);
  }

  disconnectedCallback() {
    this.input?.removeEventListener('input', this.onInput);
    this.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onDocClick);
    this.controller?.abort();
    clearTimeout(this.debounce);
  }

  onInput() {
    clearTimeout(this.debounce);

    // One or two characters match most of the catalogue, which is a panel of
    // noise and a request for every keystroke of someone's first word.
    if (this.input.value.trim().length < MIN_QUERY) {
      this.close();
      return;
    }

    this.debounce = setTimeout(() => this.search(this.input.value.trim()), DEBOUNCE_MS);
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      this.input.focus();
    }
  }

  onDocClick(event) {
    if (!this.contains(event.target)) this.close();
  }

  async search(terms) {
    this.controller?.abort();
    this.controller = new AbortController();
    const id = ++this.requestId;

    const url = new URL(window.Shopify?.routes?.root || '/', window.location.origin);
    const params = new URLSearchParams({
      q: terms,
      'resources[type]': 'product,article,page',
      'resources[limit]': '4',
      section_id: 'predictive-search',
    });

    try {
      const response = await fetch(`${url.pathname}search/suggest?${params}`, {
        signal: this.controller.signal,
      });
      if (!response.ok) throw new Error(String(response.status));
      const markup = await response.text();

      // A slow early response must never overwrite a newer one — otherwise the
      // panel ends up showing results for a prefix of what is in the field.
      if (id !== this.requestId) return;

      const fresh = new DOMParser()
        .parseFromString(markup, 'text/html')
        .querySelector('.shopify-section');

      this.panel.innerHTML = fresh ? fresh.innerHTML : '';
      this.open();
    } catch (error) {
      // A failed suggestion is not worth interrupting anyone over. The form
      // still submits, which is the whole point of it being a form.
      if (error.name !== 'AbortError') this.close();
    }
  }

  open() {
    if (!this.panel.innerHTML.trim()) return this.close();
    this.panel.hidden = false;
    this.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.panel.hidden = true;
    this.setAttribute('aria-expanded', 'false');
  }
}

customElements.define('predictive-search', PredictiveSearch);
